import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { RevealLine } from "@/components/RevealLine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-white/15 bg-black/30 px-4 py-3 text-sm text-white placeholder-white/40 backdrop-blur-md transition-colors duration-300 focus:border-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30";

  return (
    <motion.main
      data-testid="contact-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 pb-24 pt-28 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>Contact Us</RevealLine>
      </h1>
      <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
        Got a question, feedback or an idea for the app? Flick us a message and
        we'll get back to you. Prefer email?{" "}
        <a
          data-testid="contact-email-link"
          href="mailto:Support@chippymate.com.au"
          className="font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors duration-300 hover:decoration-white"
        >
          Support@chippymate.com.au
        </a>
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-12 space-y-5 rounded-2xl border border-white/10 bg-black/30 p-6 backdrop-blur-xl sm:p-8"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <input
            data-testid="contact-name-input"
            type="text"
            required
            placeholder="Your name"
            value={form.name}
            onChange={update("name")}
            className={inputClass}
          />
          <input
            data-testid="contact-email-input"
            type="email"
            required
            placeholder="Your email"
            value={form.email}
            onChange={update("email")}
            className={inputClass}
          />
        </div>
        <textarea
          data-testid="contact-message-input"
          required
          rows={5}
          placeholder="Your message"
          value={form.message}
          onChange={update("message")}
          className={`${inputClass} resize-none`}
        />
        <motion.button
          data-testid="contact-submit-button"
          type="submit"
          disabled={sending}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          className="rounded-full bg-white px-8 py-3 text-sm font-bold tracking-wide text-[#1b0b3d] transition-opacity duration-300 hover:opacity-90 disabled:opacity-50"
        >
          {sending ? "Sending..." : "Send Message"}
        </motion.button>
      </form>
    </motion.main>
  );
}
